import React, { useState } from 'react';
import { useAuth } from '@/App';
import { useApiData } from '@/hooks';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import {
  Users, Clock, DollarSign, TrendingUp, TrendingDown, Activity,
  Briefcase, Target, Lightbulb, AlertTriangle, CheckCircle2, 
  BarChart3, PieChart, RefreshCw, Sparkles, Calculator, Brain,
  ArrowRight, ChevronRight, Zap, CalendarDays, UserCheck, AlertCircle,
  Download, FileSpreadsheet
} from 'lucide-react';
import {
  LineChart, Line, AreaChart, Area, BarChart, Bar, PieChart as RechartsPieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend
} from 'recharts';
import DataFixToolbar from '@/components/common/DataFixToolbar';
import AdvancedAIPrediction from '@/components/labor/AdvancedAIPrediction';
import WeeklyOperationsEntry from '@/components/labor/WeeklyOperationsEntry';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Color constants
const COLORS = {
  fte: '#06b6d4',     // Cyan
  prn: '#f59e0b',     // Amber
  optimal: '#10b981', // Emerald
  warning: '#f59e0b', // Amber
  danger: '#ef4444',  // Red
};

// Custom tooltip for charts
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-bold mb-2">{label}</p>
        {payload.map((entry, idx) => (
          <p key={idx} className="text-sm" style={{ color: entry.color }}>
            {entry.name}: {typeof entry.value === 'number' && entry.name.includes('$') 
              ? `$${entry.value.toLocaleString()}` 
              : entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon: Icon, color, trend, trendValue }) => (
  <Card 
    className={`bg-slate-900/80 backdrop-blur-xl border-${color}-500/30`}
    style={{ boxShadow: `0 0 20px rgba(var(--${color}-rgb), 0.2)` }}
  >
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <p className={`text-sm text-${color}-400 font-bold mb-1`}>{title}</p>
          <p className="text-3xl font-black text-white">{value}</p>
          {subtitle && <p className="text-sm text-slate-400 mt-1">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center gap-1 mt-2 text-sm ${trend === 'up' ? 'text-emerald-400' : 'text-rose-400'}`}>
              {trend === 'up' ? <TrendingUp className="h-4 w-4" /> : <TrendingDown className="h-4 w-4" />}
              {trendValue}
            </div>
          )}
        </div>
        <div className={`p-3 bg-gradient-to-br from-${color}-500 to-${color}-600 rounded-xl`}>
          <Icon className="h-6 w-6 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

// AI Insights Panel
const AIInsightsPanel = ({ token, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);

  const generateInsights = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/ai-insights`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const data = await res.json();
        setInsights(data.insights);
        toast.success('AI insights generated');
      }
    } catch (error) {
      toast.error('Failed to generate insights');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="bg-gradient-to-br from-violet-900/40 to-purple-900/40 backdrop-blur-xl border-violet-500/30">
      <CardHeader>
        <CardTitle className="flex items-center justify-between text-white font-bold">
          <div className="flex items-center gap-2">
            <Brain className="h-5 w-5 text-violet-400" />
            AI-Powered Insights
          </div>
          <Button 
            onClick={generateInsights} 
            disabled={loading}
            size="sm"
            className="bg-violet-500 hover:bg-violet-600 gap-2"
          >
            {loading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
            {insights ? 'Refresh' : 'Generate'}
          </Button>
        </CardTitle>
        <CardDescription className="text-violet-300">
          AI analysis of your labor utilization patterns
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!insights ? (
          <div className="text-center py-8">
            <Lightbulb className="h-12 w-12 text-violet-400 mx-auto mb-4 opacity-50" />
            <p className="text-slate-400">Click "Generate" to analyze your labor data with AI</p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Summary */}
            <Alert className="bg-slate-800/50 border-violet-500/30">
              <Lightbulb className="h-4 w-4 text-violet-400" />
              <AlertDescription className="text-slate-200">
                {insights.summary}
              </AlertDescription>
            </Alert>

            {/* Savings Potential */}
            {insights.cost_savings_potential > 0 && (
              <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/30">
                <div className="flex items-center gap-2 text-emerald-400 font-bold mb-1">
                  <DollarSign className="h-5 w-5" />
                  Potential Monthly Savings
                </div>
                <p className="text-3xl font-black text-emerald-400">
                  ${insights.cost_savings_potential.toLocaleString()}
                </p>
              </div>
            )}

            {/* Recommendations */}
            {insights.recommendations?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <Target className="h-4 w-4 text-cyan-400" />
                  Recommendations
                </h4>
                {insights.recommendations.map((rec, idx) => (
                  <div key={idx} className="p-3 rounded-lg bg-slate-800/50 border border-slate-700/50">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <p className="font-bold text-white">{rec.title}</p>
                        <p className="text-sm text-slate-400 mt-1">{rec.description}</p>
                      </div>
                      <Badge className={`ml-2 ${
                        rec.impact === 'high' ? 'bg-emerald-500/20 text-emerald-400' :
                        rec.impact === 'medium' ? 'bg-amber-500/20 text-amber-400' :
                        'bg-slate-500/20 text-slate-400'
                      }`}>
                        {rec.impact} impact
                      </Badge>
                    </div>
                    {rec.savings_estimate > 0 && (
                      <p className="text-sm text-emerald-400 mt-2">
                        Est. savings: ${rec.savings_estimate}/month
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Priority Actions */}
            {insights.priority_actions?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <Zap className="h-4 w-4 text-amber-400" />
                  Priority Actions
                </h4>
                <ol className="space-y-2">
                  {insights.priority_actions.map((action, idx) => (
                    <li key={idx} className="flex items-center gap-3 p-2 rounded-lg bg-slate-800/30">
                      <span className="flex items-center justify-center w-6 h-6 rounded-full bg-amber-500/20 text-amber-400 text-sm font-bold">
                        {idx + 1}
                      </span>
                      <span className="text-slate-300">{action}</span>
                    </li>
                  ))}
                </ol>
              </div>
            )}

            {/* Risks */}
            {insights.risks?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-400" />
                  Risks to Monitor
                </h4>
                {insights.risks.map((risk, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-rose-500/10">
                    <AlertTriangle className="h-4 w-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    <span className="text-sm text-rose-200">{risk}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Optimization Scenarios Component
const OptimizationScenarios = ({ token }) => {
  const { data, loading } = useApiData('/api/labor-utilization/optimization-scenarios');

  if (loading) return <Skeleton className="h-64" />;

  const scenarios = data?.scenarios || [];
  const recommended = data?.recommended;

  return (
    <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-white font-bold">
          <Calculator className="h-5 w-5 text-cyan-400" />
          Optimization Scenarios
        </CardTitle>
        <CardDescription className="text-slate-400">
          What-if analysis for labor cost optimization
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {scenarios.map((scenario) => (
            <div 
              key={scenario.id}
              className={`p-4 rounded-xl border transition-all ${
                scenario.id === recommended 
                  ? 'bg-emerald-500/10 border-emerald-500/30' 
                  : scenario.id === 'current'
                  ? 'bg-slate-800/50 border-slate-700/50'
                  : 'bg-slate-800/30 border-slate-700/30 hover:bg-slate-800/50'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <h4 className="font-bold text-white">{scenario.name}</h4>
                    {scenario.id === recommended && (
                      <Badge className="bg-emerald-500/20 text-emerald-400">Recommended</Badge>
                    )}
                    {scenario.id === 'current' && (
                      <Badge className="bg-slate-500/20 text-slate-400">Current</Badge>
                    )}
                  </div>
                  <p className="text-sm text-slate-400 mt-1">{scenario.description}</p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-white">
                    ${scenario.total_cost?.toLocaleString()}
                  </p>
                  <p className="text-sm text-slate-400">
                    ${scenario.cost_per_visit?.toFixed(2)}/visit
                  </p>
                  {scenario.savings > 0 && (
                    <p className="text-sm text-emerald-400 font-bold mt-1">
                      +${scenario.savings?.toLocaleString()} savings
                    </p>
                  )}
                </div>
              </div>
              <div className="flex gap-4 mt-3 text-sm">
                <span className="text-cyan-400">FTE: {scenario.fte_hours} hrs</span>
                <span className="text-amber-400">PRN: {scenario.prn_hours} hrs</span>
              </div>
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
};

// PRN Hours Calculator Component
const PRNHoursCalculator = ({ token }) => {
  const { data, loading, refetch } = useApiData('/api/labor-utilization/prn-hours-calculator?weeks_history=8');
  const [aiPrediction, setAiPrediction] = useState(null);
  const [predicting, setPredicting] = useState(false);

  const generatePrediction = async () => {
    setPredicting(true);
    try {
      const res = await fetch(`${API_URL}/api/labor-utilization/prn-hours-ai-prediction?forecast_weeks=4`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      if (res.ok) {
        const result = await res.json();
        setAiPrediction(result.prediction);
        toast.success('AI prediction generated');
      }
    } catch (error) {
      toast.error('Failed to generate prediction');
    } finally {
      setPredicting(false);
    }
  };

  if (loading) return <Skeleton className="h-96" />;

  const metrics = data?.current_metrics || {};
  const recommendation = data?.prn_hours_recommendation || {};
  const breakdown = data?.weekly_breakdown || [];
  const basis = data?.calculation_basis || {};

  return (
    <div className="space-y-6">
      {/* Calculation Basis Info */}
      <Alert className="bg-cyan-900/20 border-cyan-500/30">
        <AlertCircle className="h-4 w-4 text-cyan-400" />
        <AlertTitle className="text-cyan-400 font-bold">PRN Hours Calculation Based On:</AlertTitle>
        <AlertDescription className="text-cyan-200 text-sm">
          • {basis.shift_hours || 8}-hour shifts • {basis.visits_per_hour || 2.5} visits/hour • {basis.post_admission_visits_per_patient || 3} visits per new admission
        </AlertDescription>
      </Alert>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Clock className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-amber-400 font-bold">Weekly PRN Hours</p>
                <p className="text-3xl font-black text-white">{recommendation.weekly_hours_needed || 0}</p>
                <p className="text-xs text-slate-400">{recommendation.weekly_shifts_needed || 0} shifts</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-rose-500/20 to-pink-500/20 border border-rose-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500/20 rounded-lg">
                <AlertTriangle className="h-5 w-5 text-rose-400" />
              </div>
              <div>
                <p className="text-sm text-rose-400 font-bold">RN Over Capacity</p>
                <p className="text-3xl font-black text-white">{metrics.total_over_capacity_rns || 0}</p>
                <p className="text-xs text-slate-400">{metrics.avg_over_capacity_visits_per_week || 0} visits/wk</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-cyan-500/20 to-teal-500/20 border border-cyan-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-cyan-500/20 rounded-lg">
                <UserCheck className="h-5 w-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-sm text-cyan-400 font-bold">Post-Admission Visits</p>
                <p className="text-3xl font-black text-white">{Math.round(metrics.avg_post_admission_visits_per_week || 0)}</p>
                <p className="text-xs text-slate-400">{metrics.avg_weekly_admissions || 0} admits/wk</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-violet-500/20 to-purple-500/20 border border-violet-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-violet-400 font-bold">Monthly Cost Est.</p>
                <p className="text-3xl font-black text-white">${(recommendation.monthly_cost_estimate || 0).toLocaleString()}</p>
                <p className="text-xs text-slate-400">{recommendation.prn_fte_equivalent || 0} FTE equiv.</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Weekly Breakdown Table */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="flex items-center justify-between text-white font-bold">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-amber-400" />
              Weekly PRN Hours Breakdown
            </div>
            <Button
              onClick={generatePrediction}
              disabled={predicting}
              size="sm"
              className="bg-violet-500 hover:bg-violet-600 gap-2"
            >
              {predicting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              AI Predict
            </Button>
          </CardTitle>
          <CardDescription className="text-slate-400">
            Based on RN over-capacity and post-admission visits
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-2 text-slate-300 font-bold">Week</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Over Capacity Visits</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Post-Admission Visits</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Total PRN Visits</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">PRN Hours</th>
                  <th className="text-center py-3 px-2 text-slate-300 font-bold">8-hr Shifts</th>
                </tr>
              </thead>
              <tbody>
                {breakdown.map((week, idx) => (
                  <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-3 px-2 text-white font-medium">{week.week}</td>
                    <td className="py-3 px-2 text-right text-rose-400">{week.rn_over_capacity_visits}</td>
                    <td className="py-3 px-2 text-right text-cyan-400">{week.post_admission_visits}</td>
                    <td className="py-3 px-2 text-right text-white font-bold">{week.total_prn_visits_needed}</td>
                    <td className="py-3 px-2 text-right text-amber-400 font-bold">{week.prn_hours_needed}</td>
                    <td className="py-3 px-2 text-center">
                      <Badge className="bg-amber-500/20 text-amber-400">{week.shifts_needed}</Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Weekly Breakdown Chart */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-amber-400" />
            PRN Hours Trend
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={breakdown}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Bar dataKey="rn_over_capacity_visits" name="Over Capacity Visits" fill="#f43f5e" stackId="visits" />
                <Bar dataKey="post_admission_visits" name="Post-Admission Visits" fill="#06b6d4" stackId="visits" />
                <Bar dataKey="prn_hours_needed" name="PRN Hours" fill="#f59e0b" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      {/* AI Prediction Results */}
      {aiPrediction && (
        <Card className="bg-gradient-to-br from-violet-900/30 to-purple-900/30 border border-violet-500/30">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Brain className="h-5 w-5 text-violet-400" />
              AI Weekly PRN Prediction
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <Alert className="bg-slate-800/50 border-violet-500/30">
              <Lightbulb className="h-4 w-4 text-violet-400" />
              <AlertDescription className="text-slate-200">
                {aiPrediction.summary}
              </AlertDescription>
            </Alert>

            {/* Weekly Predictions Table */}
            {aiPrediction.weekly_predictions && (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-2 text-slate-300 font-bold">Week</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">Predicted Hours</th>
                      <th className="text-center py-3 px-2 text-slate-300 font-bold">8-hr Shifts</th>
                      <th className="text-center py-3 px-2 text-slate-300 font-bold">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {aiPrediction.weekly_predictions.map((pred, idx) => (
                      <tr key={idx} className="border-b border-slate-800">
                        <td className="py-3 px-2 text-white">{pred.week}</td>
                        <td className="py-3 px-2 text-right text-amber-400 font-bold">{pred.predicted_hours}</td>
                        <td className="py-3 px-2 text-center">
                          <Badge className="bg-amber-500/20 text-amber-400">{pred.shifts_needed}</Badge>
                        </td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={pred.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                            {pred.confidence}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Recommendations */}
            {aiPrediction.recommendations && aiPrediction.recommendations.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <Target className="h-4 w-4 text-cyan-400" />
                  Scheduling Recommendations
                </h4>
                <ul className="space-y-2">
                  {aiPrediction.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
                      <ChevronRight className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Risk Factors */}
            {aiPrediction.risk_factors && aiPrediction.risk_factors.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-bold text-white flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4 text-rose-400" />
                  Risk Factors
                </h4>
                {aiPrediction.risk_factors.map((risk, idx) => (
                  <div key={idx} className="flex items-start gap-2 p-2 rounded-lg bg-rose-500/10 text-sm text-rose-200">
                    <AlertTriangle className="h-4 w-4 text-rose-400 mt-0.5 flex-shrink-0" />
                    {risk}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// PRN Analytics Component
const PRNAnalytics = ({ token }) => {
  const { data, loading, refetch } = useApiData('/api/labor-utilization/prn-analytics?weeks=8');

  if (loading) return <Skeleton className="h-96" />;

  const prnStaff = data?.prn_staff || [];
  const summary = data?.summary || {};
  const alerts = data?.alerts || [];

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-amber-500/10 border border-amber-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-500/20 rounded-lg">
                <Users className="h-5 w-5 text-amber-400" />
              </div>
              <div>
                <p className="text-sm text-amber-400 font-bold">Total PRN Staff</p>
                <p className="text-2xl font-black text-white">{summary.total_prn_staff || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-rose-500/10 border border-rose-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-rose-500/20 rounded-lg">
                <AlertCircle className="h-5 w-5 text-rose-400" />
              </div>
              <div>
                <p className="text-sm text-rose-400 font-bold">Over Hours Limit</p>
                <p className="text-2xl font-black text-white">{summary.staff_over_hours || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-violet-500/10 border border-violet-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-violet-500/20 rounded-lg">
                <DollarSign className="h-5 w-5 text-violet-400" />
              </div>
              <div>
                <p className="text-sm text-violet-400 font-bold">Overtime Cost</p>
                <p className="text-2xl font-black text-white">${(summary.total_overtime_cost || 0).toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-500/10 border border-emerald-500/30">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-500/20 rounded-lg">
                <CheckCircle2 className="h-5 w-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-sm text-emerald-400 font-bold">Avg Compliance</p>
                <p className="text-2xl font-black text-white">{summary.avg_compliance_rate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Alerts */}
      {alerts.length > 0 && (
        <Card className="bg-rose-900/20 border border-rose-500/30">
          <CardHeader className="pb-2">
            <CardTitle className="text-rose-400 font-bold flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              PRN Overtime Alerts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {alerts.map((alert, idx) => (
                <div key={idx} className={`flex items-center justify-between p-3 rounded-lg ${
                  alert.severity === 'critical' ? 'bg-rose-500/20' : 'bg-amber-500/20'
                }`}>
                  <div className="flex items-center gap-3">
                    <AlertCircle className={`h-5 w-5 ${
                      alert.severity === 'critical' ? 'text-rose-400' : 'text-amber-400'
                    }`} />
                    <div>
                      <p className="font-bold text-white">{alert.name}</p>
                      <p className="text-sm text-slate-400">{alert.message}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-rose-400 font-bold">+{alert.overtime_hours} hrs</p>
                    <p className="text-sm text-slate-400">${alert.overtime_cost} extra</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* PRN Staff Detail Table */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <Clock className="h-5 w-5 text-amber-400" />
            PRN Staff Hours & Visits Tracking
          </CardTitle>
          <CardDescription className="text-slate-400">
            Individual PRN staff metrics with hour/visit limits (24 hrs / 15 visits per week)
          </CardDescription>
        </CardHeader>
        <CardContent>
          {prnStaff.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 text-slate-500 mx-auto mb-3" />
              <p className="text-slate-400">No PRN staff data available</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-700">
                    <th className="text-left py-3 px-2 text-slate-300 font-bold">Name</th>
                    <th className="text-center py-3 px-2 text-slate-300 font-bold">Discipline</th>
                    <th className="text-right py-3 px-2 text-slate-300 font-bold">Avg Hours</th>
                    <th className="text-right py-3 px-2 text-slate-300 font-bold">Avg Visits</th>
                    <th className="text-center py-3 px-2 text-slate-300 font-bold">Over Hours</th>
                    <th className="text-center py-3 px-2 text-slate-300 font-bold">Over Visits</th>
                    <th className="text-right py-3 px-2 text-slate-300 font-bold">OT Cost</th>
                    <th className="text-center py-3 px-2 text-slate-300 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {prnStaff.map((staff, idx) => (
                    <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                      <td className="py-3 px-2">
                        <div className="flex items-center gap-2">
                          <div className={`w-2 h-2 rounded-full ${
                            staff.alert_level === 'critical' ? 'bg-rose-500' :
                            staff.alert_level === 'warning' ? 'bg-amber-500' : 'bg-emerald-500'
                          }`} />
                          <span className="text-white font-medium">{staff.name}</span>
                        </div>
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge className="bg-slate-700 text-slate-300">{staff.discipline}</Badge>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className={staff.avg_hours > staff.hour_limit ? 'text-rose-400 font-bold' : 'text-white'}>
                          {staff.avg_hours}
                        </span>
                        <span className="text-slate-500 text-sm">/{staff.hour_limit}</span>
                      </td>
                      <td className="py-3 px-2 text-right">
                        <span className={staff.avg_visits > staff.visit_limit ? 'text-rose-400 font-bold' : 'text-white'}>
                          {staff.avg_visits}
                        </span>
                        <span className="text-slate-500 text-sm">/{staff.visit_limit}</span>
                      </td>
                      <td className="py-3 px-2 text-center">
                        {staff.weeks_over_hours > 0 ? (
                          <Badge className="bg-rose-500/20 text-rose-400">{staff.weeks_over_hours} weeks</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-400">0</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-center">
                        {staff.weeks_over_visits > 0 ? (
                          <Badge className="bg-amber-500/20 text-amber-400">{staff.weeks_over_visits} weeks</Badge>
                        ) : (
                          <Badge className="bg-emerald-500/20 text-emerald-400">0</Badge>
                        )}
                      </td>
                      <td className="py-3 px-2 text-right text-rose-400 font-bold">
                        ${staff.overtime_cost}
                      </td>
                      <td className="py-3 px-2 text-center">
                        <Badge className={`${
                          staff.alert_level === 'critical' ? 'bg-rose-500/20 text-rose-400' :
                          staff.alert_level === 'warning' ? 'bg-amber-500/20 text-amber-400' :
                          'bg-emerald-500/20 text-emerald-400'
                        }`}>
                          {staff.compliance_rate}%
                        </Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* PRN Hours Trend Chart */}
      {prnStaff.length > 0 && prnStaff[0].weekly_trend?.length > 0 && (
        <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-amber-400" />
              PRN Hours Trend (Top Staff)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={prnStaff[0].weekly_trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                  <YAxis stroke="#94a3b8" fontSize={12} />
                  <Tooltip content={<CustomTooltip />} />
                  <Legend />
                  <Bar dataKey="hours" name="Hours Worked" fill={COLORS.prn} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="over_hours" name="Over Limit" fill={COLORS.danger} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Scheduling Optimizer Component
const SchedulingOptimizer = ({ token }) => {
  const { data, loading } = useApiData('/api/labor-utilization/scheduling-optimizer?forecast_weeks=4');

  if (loading) return <Skeleton className="h-96" />;

  const currentState = data?.current_state || {};
  const forecasts = data?.weekly_forecasts || [];
  const recommendations = data?.recommendations || [];
  const summary = data?.summary || {};

  return (
    <div className="space-y-6">
      {/* Current State */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="bg-cyan-500/10 border border-cyan-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-cyan-400 font-bold">Current Census</p>
            <p className="text-3xl font-black text-white">{currentState.census}</p>
            <div className={`flex items-center gap-1 mt-1 text-sm ${
              currentState.trend === 'increasing' ? 'text-emerald-400' : 
              currentState.trend === 'decreasing' ? 'text-rose-400' : 'text-slate-400'
            }`}>
              {currentState.trend === 'increasing' ? <TrendingUp className="h-4 w-4" /> : 
               currentState.trend === 'decreasing' ? <TrendingDown className="h-4 w-4" /> : null}
              {currentState.trend} ({currentState.trend_rate}%)
            </div>
          </CardContent>
        </Card>

        <Card className="bg-emerald-500/10 border border-emerald-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-emerald-400 font-bold">Current FTE</p>
            <p className="text-3xl font-black text-white">{currentState.current_fte}</p>
            <p className="text-sm text-slate-400 mt-1">
              Gap: {summary.staffing_gap?.fte > 0 ? `+${summary.staffing_gap.fte} needed` : 'Adequate'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-amber-500/10 border border-amber-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-amber-400 font-bold">Current PRN</p>
            <p className="text-3xl font-black text-white">{currentState.current_prn}</p>
            <p className="text-sm text-slate-400 mt-1">
              Gap: {summary.staffing_gap?.prn > 0 ? `+${summary.staffing_gap.prn} needed` : 'Adequate'}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-violet-500/10 border border-violet-500/30">
          <CardContent className="p-4">
            <p className="text-sm text-violet-400 font-bold">Forecast Cost</p>
            <p className="text-3xl font-black text-white">${(summary.total_forecast_labor_cost || 0).toLocaleString()}</p>
            <p className="text-sm text-slate-400 mt-1">Next 4 weeks</p>
          </CardContent>
        </Card>
      </div>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <Card className="bg-gradient-to-br from-violet-900/30 to-purple-900/30 border border-violet-500/30">
          <CardHeader>
            <CardTitle className="text-white font-bold flex items-center gap-2">
              <Lightbulb className="h-5 w-5 text-violet-400" />
              Scheduling Recommendations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {recommendations.map((rec, idx) => (
                <div key={idx} className="flex items-start gap-4 p-4 rounded-xl bg-slate-800/50 border border-slate-700/50">
                  <div className={`p-2 rounded-lg ${
                    rec.priority === 'high' ? 'bg-rose-500/20' : 'bg-amber-500/20'
                  }`}>
                    {rec.type === 'hire' ? <UserCheck className="h-5 w-5 text-emerald-400" /> :
                     rec.type === 'prepare' ? <CalendarDays className="h-5 w-5 text-cyan-400" /> :
                     <Target className="h-5 w-5 text-amber-400" />}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-bold text-white">{rec.title}</h4>
                      <Badge className={rec.priority === 'high' ? 'bg-rose-500/20 text-rose-400' : 'bg-amber-500/20 text-amber-400'}>
                        {rec.priority}
                      </Badge>
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{rec.description}</p>
                    <p className="text-sm text-emerald-400 mt-2">{rec.impact}</p>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Weekly Forecasts */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-cyan-400" />
            Weekly Staffing Forecast
          </CardTitle>
          <CardDescription className="text-slate-400">
            Predicted staffing needs based on census forecast
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-slate-700">
                  <th className="text-left py-3 px-2 text-slate-300 font-bold">Week</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Census</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Visits</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">FTE Needed</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">PRN Needed</th>
                  <th className="text-right py-3 px-2 text-slate-300 font-bold">Labor Cost</th>
                  <th className="text-center py-3 px-2 text-slate-300 font-bold">Confidence</th>
                </tr>
              </thead>
              <tbody>
                {forecasts.map((forecast, idx) => (
                  <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                    <td className="py-3 px-2 text-white font-medium">{forecast.week}</td>
                    <td className="py-3 px-2 text-right text-cyan-400 font-bold">{forecast.forecast_census}</td>
                    <td className="py-3 px-2 text-right text-white">{forecast.required_visits}</td>
                    <td className="py-3 px-2 text-right text-emerald-400 font-bold">{forecast.recommended_fte}</td>
                    <td className="py-3 px-2 text-right text-amber-400 font-bold">{forecast.recommended_prn}</td>
                    <td className="py-3 px-2 text-right text-white">${forecast.estimated_labor_cost.toLocaleString()}</td>
                    <td className="py-3 px-2 text-center">
                      <Badge className={forecast.confidence === 'high' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-amber-500/20 text-amber-400'}>
                        {forecast.confidence}
                      </Badge>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Forecast Chart */}
      <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
        <CardHeader>
          <CardTitle className="text-white font-bold flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-emerald-400" />
            Census & Staffing Forecast
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={forecasts}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                <YAxis stroke="#94a3b8" fontSize={12} />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                <Line type="monotone" dataKey="forecast_census" name="Census" stroke="#06b6d4" strokeWidth={3} dot={{ r: 4 }} />
                <Line type="monotone" dataKey="recommended_fte" name="FTE Needed" stroke="#10b981" strokeWidth={2} />
                <Line type="monotone" dataKey="recommended_prn" name="PRN Needed" stroke="#f59e0b" strokeWidth={2} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Main Page Component
export default function LaborUtilization() {
  const { token } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [exporting, setExporting] = useState(false);

  // Fetch data
  const { data: summary, loading: summaryLoading, refetch: refetchSummary } = useApiData(
    '/api/labor-utilization/summary',
    { cache: false }
  );
  const { data: trends, loading: trendsLoading } = useApiData(
    '/api/labor-utilization/trends?weeks=12',
    { cache: true }
  );
  const { data: staffBreakdown, loading: staffLoading } = useApiData(
    '/api/labor-utilization/staff-breakdown',
    { cache: true }
  );

  // Export function
  const handleExport = async (format = 'csv') => {
    setExporting(true);
    try {
      const response = await fetch(
        `${API_URL}/api/labor-utilization/export/historical?format=${format}&weeks=12`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      if (!response.ok) throw new Error('Export failed');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `labor_utilization_export_${new Date().toISOString().split('T')[0]}.${format}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success(`Labor data exported successfully as ${format.toUpperCase()}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  // Prepare chart data
  const pieData = summary ? [
    { name: 'FTE Hours', value: summary.summary?.fte_hours || 0, color: COLORS.fte },
    { name: 'PRN Hours', value: summary.summary?.prn_hours || 0, color: COLORS.prn },
  ] : [];

  const trendData = trends?.trends || [];
  const staffList = staffBreakdown?.staff || [];

  if (summaryLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48" />
        <div className="grid grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => <Skeleton key={i} className="h-32" />)}
        </div>
      </div>
    );
  }

  const utilizationData = summary?.utilization || {};
  const costsData = summary?.costs || {};
  const benchmarks = summary?.benchmarks || {};
  const status = summary?.status || {};

  return (
    <div className="space-y-6" data-testid="labor-utilization-page">
      {/* Futuristic Header */}
      <div className="relative overflow-hidden rounded-3xl p-8 text-white">
        <div className="absolute inset-0 bg-gradient-to-r from-cyan-600 via-teal-600 to-emerald-600 animate-gradient-x" />
        <div className="absolute inset-0 opacity-20" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, white 1px, transparent 1px),
                            radial-gradient(circle at 75% 75%, white 1px, transparent 1px)`,
          backgroundSize: '60px 60px'
        }} />
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-white/20 rounded-full blur-3xl" />
        <div className="absolute bottom-0 right-1/4 w-64 h-64 bg-white/15 rounded-full blur-3xl" />

        <div className="relative z-10 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-4xl font-black tracking-tight flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm shadow-lg">
                <Clock className="h-8 w-8" />
              </div>
              <span className="drop-shadow-lg">FTE vs PRN Labor Utilization</span>
            </h1>
            <p className="text-white/80 mt-3 text-lg font-medium">
              AI-powered labor cost analysis and optimization insights
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={() => handleExport('csv')}
              disabled={exporting}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-white/30 gap-2"
              data-testid="export-csv-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <FileSpreadsheet className="h-4 w-4" />}
              Export CSV
            </Button>
            <Button
              onClick={() => handleExport('json')}
              disabled={exporting}
              className="bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-white/30 gap-2"
              data-testid="export-json-btn"
            >
              {exporting ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              Export JSON
            </Button>
          </div>
        </div>
      </div>

      {/* Data Fix Toolbar */}
      <DataFixToolbar onDataFixed={refetchSummary} variant="compact" />

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="bg-slate-900/80 backdrop-blur-xl border-cyan-500/30" style={{ boxShadow: '0 0 20px rgba(6, 182, 212, 0.2)' }}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-cyan-400 font-bold mb-1">FTE Hours</p>
                <p className="text-3xl font-black text-white">{summary?.summary?.fte_hours || 0}</p>
                <p className="text-sm text-slate-400 mt-1">{utilizationData.fte_percent}% of total</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-cyan-500 to-cyan-600 rounded-xl">
                <Briefcase className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-amber-500/30" style={{ boxShadow: '0 0 20px rgba(245, 158, 11, 0.2)' }}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-amber-400 font-bold mb-1">PRN Hours</p>
                <p className="text-3xl font-black text-white">{summary?.summary?.prn_hours || 0}</p>
                <p className="text-sm text-slate-400 mt-1">{utilizationData.prn_percent}% of total</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-amber-500 to-amber-600 rounded-xl">
                <Clock className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-emerald-500/30" style={{ boxShadow: '0 0 20px rgba(16, 185, 129, 0.2)' }}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-emerald-400 font-bold mb-1">Cost Per Visit</p>
                <p className="text-3xl font-black text-white">${costsData.cost_per_visit}</p>
                <p className={`text-sm mt-1 ${benchmarks.cost_variance <= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {benchmarks.cost_variance <= 0 ? '✓' : '↑'} Target: ${benchmarks.target_cost_per_visit}
                </p>
              </div>
              <div className="p-3 bg-gradient-to-br from-emerald-500 to-emerald-600 rounded-xl">
                <DollarSign className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-slate-900/80 backdrop-blur-xl border-violet-500/30" style={{ boxShadow: '0 0 20px rgba(139, 92, 246, 0.2)' }}>
          <CardContent className="p-5">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-violet-400 font-bold mb-1">Total Labor Cost</p>
                <p className="text-3xl font-black text-white">${(costsData.total_labor_cost || 0).toLocaleString()}</p>
                <p className="text-sm text-slate-400 mt-1">{summary?.summary?.total_visits} visits</p>
              </div>
              <div className="p-3 bg-gradient-to-br from-violet-500 to-violet-600 rounded-xl">
                <BarChart3 className="h-6 w-6 text-white" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-2 p-2 bg-slate-900/60 backdrop-blur-xl rounded-2xl border border-slate-700/50">
          <TabsTrigger value="overview" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-cyan-500 data-[state=active]:to-teal-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <PieChart className="h-4 w-4" /> Overview
          </TabsTrigger>
          <TabsTrigger value="weekly-data" data-testid="weekly-data-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-teal-500 data-[state=active]:to-cyan-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <CalendarDays className="h-4 w-4" /> Weekly Data Entry
          </TabsTrigger>
          <TabsTrigger value="prn-analytics" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-amber-500 data-[state=active]:to-orange-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <AlertCircle className="h-4 w-4" /> PRN Analytics
          </TabsTrigger>
          <TabsTrigger value="scheduling" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-emerald-500 data-[state=active]:to-green-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Target className="h-4 w-4" /> Scheduling
          </TabsTrigger>
          <TabsTrigger value="trends" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-rose-500 data-[state=active]:to-pink-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <TrendingUp className="h-4 w-4" /> Trends
          </TabsTrigger>
          <TabsTrigger value="staff" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Users className="h-4 w-4" /> Staff Breakdown
          </TabsTrigger>
          <TabsTrigger value="insights" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-indigo-500 data-[state=active]:to-blue-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Brain className="h-4 w-4" /> AI Insights
          </TabsTrigger>
          <TabsTrigger value="prn-calculator" data-testid="prn-calculator-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-red-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Calculator className="h-4 w-4" /> PRN Calculator
          </TabsTrigger>
          <TabsTrigger value="advanced-ai" data-testid="advanced-ai-tab" className="gap-2 data-[state=active]:bg-gradient-to-r data-[state=active]:from-violet-500 data-[state=active]:to-purple-600 data-[state=active]:text-white rounded-xl px-4 py-2 font-bold">
            <Sparkles className="h-4 w-4" /> Advanced AI
          </TabsTrigger>
        </TabsList>
        
        {/* Weekly Data Entry Tab */}
        <TabsContent value="weekly-data" className="space-y-6 mt-6" data-testid="weekly-data-content">
          <WeeklyOperationsEntry token={token} />
        </TabsContent>

        {/* Overview Tab */}
        <TabsContent value="overview" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* FTE/PRN Distribution Pie Chart */}
            <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white font-bold flex items-center gap-2">
                  <PieChart className="h-5 w-5 text-cyan-400" />
                  Hours Distribution
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <RechartsPieChart>
                      <Pie
                        data={pieData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {pieData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                    </RechartsPieChart>
                  </ResponsiveContainer>
                </div>
                <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                  <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/30">
                    <p className="text-sm text-cyan-400 font-bold">FTE Rate</p>
                    <p className="text-xl font-black text-white">${costsData.fte_hourly_rate}/hr</p>
                  </div>
                  <div className="p-3 rounded-lg bg-amber-500/10 border border-amber-500/30">
                    <p className="text-sm text-amber-400 font-bold">PRN Rate</p>
                    <p className="text-xl font-black text-white">${costsData.prn_hourly_rate}/hr</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Benchmark Comparison */}
            <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
              <CardHeader>
                <CardTitle className="text-white font-bold flex items-center gap-2">
                  <Target className="h-5 w-5 text-emerald-400" />
                  Benchmark Comparison
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* FTE Mix */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">FTE Mix</span>
                    <span className="text-white font-bold">{utilizationData.fte_percent}% / {benchmarks.target_fte_percent}% target</span>
                  </div>
                  <Progress 
                    value={utilizationData.fte_percent} 
                    className="h-3 bg-slate-700"
                  />
                  <div className="flex justify-between text-xs mt-1">
                    <span className={benchmarks.fte_variance >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                      {benchmarks.fte_variance >= 0 ? '+' : ''}{benchmarks.fte_variance}% variance
                    </span>
                    <span className="text-slate-500">Industry: 75% FTE</span>
                  </div>
                </div>

                {/* Cost Per Visit */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Cost Per Visit</span>
                    <span className="text-white font-bold">${costsData.cost_per_visit} / ${benchmarks.target_cost_per_visit} target</span>
                  </div>
                  <Progress 
                    value={Math.min((benchmarks.target_cost_per_visit / costsData.cost_per_visit) * 100, 100)} 
                    className="h-3 bg-slate-700"
                  />
                  <div className="flex justify-between text-xs mt-1">
                    <span className={benchmarks.cost_variance <= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                      {benchmarks.cost_variance <= 0 ? '' : '+'}${benchmarks.cost_variance} variance
                    </span>
                    <span className="text-slate-500">Target: $55/visit</span>
                  </div>
                </div>

                {/* Productivity */}
                <div>
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Visits Per Hour</span>
                    <span className="text-white font-bold">{utilizationData.visits_per_hour} / {benchmarks.target_visits_per_hour} target</span>
                  </div>
                  <Progress 
                    value={(utilizationData.visits_per_hour / benchmarks.target_visits_per_hour) * 100} 
                    className="h-3 bg-slate-700"
                  />
                  <div className="flex justify-between text-xs mt-1">
                    <span className={benchmarks.productivity_variance >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                      {benchmarks.productivity_variance >= 0 ? '+' : ''}{benchmarks.productivity_variance} variance
                    </span>
                    <span className="text-slate-500">Target: 0.5 visits/hr</span>
                  </div>
                </div>

                {/* Status Indicators */}
                <div className="grid grid-cols-3 gap-2 mt-4">
                  <div className={`p-2 rounded-lg text-center ${
                    status.fte_status === 'optimal' ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-amber-500/20 border border-amber-500/30'
                  }`}>
                    <p className="text-xs text-slate-400">FTE Mix</p>
                    <p className={`text-sm font-bold ${status.fte_status === 'optimal' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {status.fte_status === 'optimal' ? '✓ Optimal' : status.fte_status}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${
                    status.cost_status === 'optimal' ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-rose-500/20 border border-rose-500/30'
                  }`}>
                    <p className="text-xs text-slate-400">Cost</p>
                    <p className={`text-sm font-bold ${status.cost_status === 'optimal' ? 'text-emerald-400' : 'text-rose-400'}`}>
                      {status.cost_status === 'optimal' ? '✓ On Budget' : 'Over'}
                    </p>
                  </div>
                  <div className={`p-2 rounded-lg text-center ${
                    status.productivity_status === 'optimal' ? 'bg-emerald-500/20 border border-emerald-500/30' : 'bg-amber-500/20 border border-amber-500/30'
                  }`}>
                    <p className="text-xs text-slate-400">Productivity</p>
                    <p className={`text-sm font-bold ${status.productivity_status === 'optimal' ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {status.productivity_status === 'optimal' ? '✓ Optimal' : 'Below'}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Optimization Scenarios */}
          <OptimizationScenarios token={token} />
        </TabsContent>

        {/* PRN Analytics Tab */}
        <TabsContent value="prn-analytics" className="mt-6">
          <PRNAnalytics token={token} />
        </TabsContent>

        {/* Scheduling Optimizer Tab */}
        <TabsContent value="scheduling" className="mt-6">
          <SchedulingOptimizer token={token} />
        </TabsContent>

        {/* Trends Tab */}
        <TabsContent value="trends" className="space-y-6 mt-6">
          {/* Hours Trend Chart */}
          <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white font-bold flex items-center gap-2">
                <TrendingUp className="h-5 w-5 text-emerald-400" />
                Weekly Hours Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={trendData}>
                    <defs>
                      <linearGradient id="fteFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.fte} stopOpacity={0.4}/>
                        <stop offset="95%" stopColor={COLORS.fte} stopOpacity={0}/>
                      </linearGradient>
                      <linearGradient id="prnFill" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor={COLORS.prn} stopOpacity={0.4}/>
                        <stop offset="95%" stopColor={COLORS.prn} stopOpacity={0}/>
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Area type="monotone" dataKey="fte_hours" name="FTE Hours" stroke={COLORS.fte} fill="url(#fteFill)" strokeWidth={2} />
                    <Area type="monotone" dataKey="prn_hours" name="PRN Hours" stroke={COLORS.prn} fill="url(#prnFill)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Cost Per Visit Trend */}
          <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white font-bold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-emerald-400" />
                Cost Per Visit Trend
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={trendData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                    <XAxis dataKey="week" stroke="#94a3b8" fontSize={12} />
                    <YAxis stroke="#94a3b8" fontSize={12} domain={[40, 70]} />
                    <Tooltip content={<CustomTooltip />} />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="cost_per_visit" 
                      name="$ Cost Per Visit" 
                      stroke={COLORS.optimal} 
                      strokeWidth={3}
                      dot={{ fill: COLORS.optimal, strokeWidth: 2, r: 4 }}
                    />
                    {/* Target line */}
                    <Line 
                      type="monotone" 
                      dataKey={() => 55} 
                      name="Target" 
                      stroke="#64748b" 
                      strokeDasharray="5 5"
                      strokeWidth={2}
                      dot={false}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Staff Breakdown Tab */}
        <TabsContent value="staff" className="space-y-6 mt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Card className="bg-cyan-500/10 border border-cyan-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-cyan-400 font-bold">FTE Staff</p>
                    <p className="text-3xl font-black text-white">{staffBreakdown?.fte_count || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">{staffBreakdown?.totals?.fte_hours || 0} hours</p>
                    <p className="text-lg font-bold text-cyan-400">${(staffBreakdown?.totals?.fte_cost || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="bg-amber-500/10 border border-amber-500/30">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-400 font-bold">PRN Staff</p>
                    <p className="text-3xl font-black text-white">{staffBreakdown?.prn_count || 0}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-slate-400">{staffBreakdown?.totals?.prn_hours || 0} hours</p>
                    <p className="text-lg font-bold text-amber-400">${(staffBreakdown?.totals?.prn_cost || 0).toLocaleString()}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <Card className="bg-slate-900/60 backdrop-blur-xl border-slate-700/50">
            <CardHeader>
              <CardTitle className="text-white font-bold flex items-center gap-2">
                <Users className="h-5 w-5 text-violet-400" />
                Staff Labor Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-700">
                      <th className="text-left py-3 px-2 text-slate-300 font-bold">Name</th>
                      <th className="text-center py-3 px-2 text-slate-300 font-bold">Type</th>
                      <th className="text-center py-3 px-2 text-slate-300 font-bold">Discipline</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">Hours</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">Visits</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">Rate</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">Cost</th>
                      <th className="text-right py-3 px-2 text-slate-300 font-bold">$/Visit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {staffList.map((staff, idx) => (
                      <tr key={idx} className="border-b border-slate-800 hover:bg-slate-800/50">
                        <td className="py-3 px-2 text-white font-medium">{staff.name}</td>
                        <td className="py-3 px-2 text-center">
                          <Badge className={staff.employment_type === 'FTE' 
                            ? 'bg-cyan-500/20 text-cyan-400' 
                            : 'bg-amber-500/20 text-amber-400'
                          }>
                            {staff.employment_type}
                          </Badge>
                        </td>
                        <td className="py-3 px-2 text-center text-slate-400">{staff.discipline}</td>
                        <td className="py-3 px-2 text-right text-white">{staff.hours_worked}</td>
                        <td className="py-3 px-2 text-right text-emerald-400 font-bold">{staff.visits_completed}</td>
                        <td className="py-3 px-2 text-right text-slate-400">${staff.hourly_rate}</td>
                        <td className="py-3 px-2 text-right text-white font-bold">${staff.labor_cost.toLocaleString()}</td>
                        <td className="py-3 px-2 text-right">
                          <span className={staff.cost_per_visit <= 55 ? 'text-emerald-400' : 'text-amber-400'}>
                            ${staff.cost_per_visit}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* AI Insights Tab */}
        <TabsContent value="insights" className="mt-6">
          <AIInsightsPanel token={token} onRefresh={refetchSummary} />
        </TabsContent>

        {/* PRN Calculator Tab */}
        <TabsContent value="prn-calculator" className="mt-6" data-testid="prn-calculator-content">
          <PRNHoursCalculator token={token} />
        </TabsContent>

        {/* Advanced AI Tab */}
        <TabsContent value="advanced-ai" className="mt-6" data-testid="advanced-ai-content">
          <AdvancedAIPrediction token={token} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
